// src/pages/finance-portal/sales-tax/SalesTax.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid, Tabs, Tab,
    IconButton, Tooltip, Checkbox, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

const US_STATES = [
    { value: '', label: 'All States' },
    { value: 'AL', label: 'AL' }, { value: 'AK', label: 'AK' }, { value: 'AZ', label: 'AZ' },
    { value: 'AR', label: 'AR' }, { value: 'CA', label: 'CA' }, { value: 'CO', label: 'CO' },
    { value: 'CT', label: 'CT' }, { value: 'DE', label: 'DE' }, { value: 'FL', label: 'FL' },
    { value: 'GA', label: 'GA' }, { value: 'HI', label: 'HI' }, { value: 'ID', label: 'ID' },
    { value: 'IL', label: 'IL' }, { value: 'IN', label: 'IN' }, { value: 'IA', label: 'IA' },
    { value: 'KS', label: 'KS' }, { value: 'KY', label: 'KY' }, { value: 'LA', label: 'LA' },
    { value: 'ME', label: 'ME' }, { value: 'MD', label: 'MD' }, { value: 'MA', label: 'MA' },
    { value: 'MI', label: 'MI' }, { value: 'MN', label: 'MN' }, { value: 'MS', label: 'MS' },
    { value: 'MO', label: 'MO' }, { value: 'MT', label: 'MT' }, { value: 'NE', label: 'NE' },
    { value: 'NV', label: 'NV' }, { value: 'NH', label: 'NH' }, { value: 'NJ', label: 'NJ' },
    { value: 'NM', label: 'NM' }, { value: 'NY', label: 'NY' }, { value: 'NC', label: 'NC' },
    { value: 'ND', label: 'ND' }, { value: 'OH', label: 'OH' }, { value: 'OK', label: 'OK' },
    { value: 'OR', label: 'OR' }, { value: 'PA', label: 'PA' }, { value: 'RI', label: 'RI' },
    { value: 'SC', label: 'SC' }, { value: 'SD', label: 'SD' }, { value: 'TN', label: 'TN' },
    { value: 'TX', label: 'TX' }, { value: 'UT', label: 'UT' }, { value: 'VT', label: 'VT' },
    { value: 'VA', label: 'VA' }, { value: 'WA', label: 'WA' }, { value: 'WV', label: 'WV' },
    { value: 'WI', label: 'WI' }, { value: 'WY', label: 'WY' }, { value: 'DC', label: 'DC' },
];

const JURISDICTION_LEVELS = [
    { value: '', label: 'All Levels' },
    { value: 'STATE', label: 'State' },
    { value: 'COUNTY', label: 'County' },
    { value: 'CITY', label: 'City' },
    { value: 'DISTRICT', label: 'District' },
];

const TAX_TYPES = [
    { value: 'SALES', label: 'Sales Tax' },
    { value: 'USE', label: 'Use Tax' },
    { value: 'EXCISE', label: 'Excise Tax' },
];

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function SalesTax() {
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Tax Rates State
    const [rates, setRates] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [filterState, setFilterState] = useState('');
    const [filterLevel, setFilterLevel] = useState('');

    // Rate Form Dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [formData, setFormData] = useState({
        jurisdiction_name: '', jurisdiction_level: 'STATE', state_code: '',
        rate: '', tax_type: 'SALES', effective_date: '', is_active: true,
    });
    const [saving, setSaving] = useState(false);

    // Delete Dialog
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Calculator State
    const [calcForm, setCalcForm] = useState({
        state: '', county: '', city: '', zip: '',
    });
    const [lineItems, setLineItems] = useState([{ description: '', amount: '', is_taxable: true }]);
    const [calcResult, setCalcResult] = useState(null);
    const [calculating, setCalculating] = useState(false);

    // Fetch Tax Rates
    const fetchRates = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (filterState) params.state_code = filterState;
            if (filterLevel) params.jurisdiction_level = filterLevel;
            const res = await axiosInstance.get('/api/finance/tax/rates', { params });
            if (res.data.success) {
                setRates(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load tax rates');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, filterState, filterLevel]);

    useEffect(() => { fetchRates(); }, [fetchRates]);

    const openForm = (rate) => {
        if (rate) {
            setEditData(rate);
            setFormData({
                jurisdiction_name: rate.jurisdiction_name || '',
                jurisdiction_level: rate.jurisdiction_level || 'STATE',
                state_code: rate.state_code || '',
                rate: rate.rate ?? '',
                tax_type: rate.tax_type || 'SALES',
                effective_date: rate.effective_date ? rate.effective_date.split('T')[0] : '',
                is_active: rate.is_active !== false,
            });
        } else {
            setEditData(null);
            setFormData({
                jurisdiction_name: '', jurisdiction_level: 'STATE', state_code: '',
                rate: '', tax_type: 'SALES', effective_date: '', is_active: true,
            });
        }
        setFormOpen(true);
    };

    const handleSaveRate = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                rate: parseFloat(formData.rate) || 0,
            };
            if (editData) {
                await axiosInstance.put(`/api/finance/tax/rates/${editData.id}`, payload);
            } else {
                await axiosInstance.post('/api/finance/tax/rates', payload);
            }
            setFormOpen(false);
            setSuccess(editData ? 'Tax rate updated' : 'Tax rate added');
            fetchRates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save tax rate');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/tax/rates/${deleteId}`);
            setDeleteId(null);
            setSuccess('Tax rate deleted');
            fetchRates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete tax rate');
        } finally {
            setDeleting(false);
        }
    };

    // Calculator
    const handleAddLineItem = () => {
        setLineItems(prev => [...prev, { description: '', amount: '', is_taxable: true }]);
    };

    const handleLineItemChange = (index, field, value) => {
        setLineItems(prev => {
            const items = [...prev];
            items[index] = { ...items[index], [field]: value };
            return items;
        });
    };

    const handleRemoveLineItem = (index) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleCalculate = async () => {
        setCalculating(true);
        setCalcResult(null);
        setError(null);
        try {
            const payload = {
                address: {
                    state: calcForm.state,
                    county: calcForm.county,
                    city: calcForm.city,
                    zip: calcForm.zip,
                },
                line_items: lineItems.map(li => ({
                    description: li.description,
                    amount: parseFloat(li.amount) || 0,
                    is_taxable: li.is_taxable,
                })),
            };
            const res = await axiosInstance.post('/api/finance/tax/calculate', payload);
            if (res.data.success) {
                setCalcResult(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate tax');
        } finally {
            setCalculating(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ReceiptIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Sales Tax</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage tax rates and calculate sales tax</Typography>
                        </Box>
                    </Box>
                    {activeTab === 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => openForm(null)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add Rate
                        </Button>
                    )}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                <Tabs
                    value={activeTab}
                    onChange={(_, val) => setActiveTab(val)}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#64748b', minHeight: 40 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label="Tax Rates" />
                    <Tab label="Tax Calculator" />
                </Tabs>

                {/* ==================== TAX RATES TAB ==================== */}
                <TabPanel value={activeTab} index={0}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            size="small" select label="State" value={filterState}
                            onChange={e => { setFilterState(e.target.value); setPage(0); }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            sx={{ minWidth: 130 }}
                        >
                            {US_STATES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                        </TextField>
                        <TextField
                            size="small" select label="Level" value={filterLevel}
                            onChange={e => { setFilterLevel(e.target.value); setPage(0); }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            sx={{ minWidth: 130 }}
                        >
                            {JURISDICTION_LEVELS.map(l => <MenuItem key={l.value} value={l.value} sx={{ fontFamily }}>{l.label}</MenuItem>)}
                        </TextField>
                    </Box>

                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Jurisdiction', 'Level', 'State', 'Rate', 'Type', 'Effective Date', 'Active', 'Actions'].map(h => (
                                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        [1, 2, 3, 4, 5].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                        ))
                                    ) : rates.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No tax rates found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : rates.map(r => (
                                        <TableRow key={r.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.jurisdiction_name}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={r.jurisdiction_level} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: '#e0e7ff', color: '#3730a3' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{r.state_code}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.rate != null ? `${r.rate}%` : '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{r.tax_type}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(r.effective_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={r.is_active ? 'Active' : 'Inactive'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: r.is_active ? '#dcfce7' : '#f1f5f9', color: r.is_active ? '#166534' : '#64748b' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => openForm(r)}>
                                                            <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => setDeleteId(r.id)}>
                                                            <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={total} page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ '& *': { fontFamily } }}
                        />
                    </Card>
                </TabPanel>

                {/* ==================== TAX CALCULATOR TAB ==================== */}
                <TabPanel value={activeTab} index={1}>
                    <Card elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>Address</Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={3}>
                                <TextField size="small" fullWidth select label="State" value={calcForm.state}
                                    onChange={e => setCalcForm(prev => ({ ...prev, state: e.target.value }))}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily } }}>
                                    {US_STATES.filter(s => s.value).map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField size="small" fullWidth label="County" value={calcForm.county}
                                    onChange={e => setCalcForm(prev => ({ ...prev, county: e.target.value }))}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily } }} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField size="small" fullWidth label="City" value={calcForm.city}
                                    onChange={e => setCalcForm(prev => ({ ...prev, city: e.target.value }))}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily } }} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField size="small" fullWidth label="ZIP Code" value={calcForm.zip}
                                    onChange={e => setCalcForm(prev => ({ ...prev, zip: e.target.value }))}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    InputLabelProps={{ sx: { fontFamily } }} />
                            </Grid>
                        </Grid>

                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2, color: '#1e293b' }}>Line Items</Typography>
                        <Table size="small" sx={{ mb: 2 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Description', 'Amount', 'Taxable', ''].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lineItems.map((li, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={bodyCellSx}>
                                            <TextField size="small" fullWidth placeholder="Description" value={li.description}
                                                onChange={e => handleLineItemChange(idx, 'description', e.target.value)}
                                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <TextField size="small" type="number" placeholder="0.00" value={li.amount}
                                                onChange={e => handleLineItemChange(idx, 'amount', e.target.value)}
                                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                                sx={{ width: 150 }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Checkbox checked={li.is_taxable}
                                                onChange={e => handleLineItemChange(idx, 'is_taxable', e.target.checked)}
                                                sx={{ '&.Mui-checked': { color: ACCENT } }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <IconButton size="small" onClick={() => handleRemoveLineItem(idx)} disabled={lineItems.length <= 1}>
                                                <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button startIcon={<AddIcon />} onClick={handleAddLineItem}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', color: ACCENT, '&:hover': { bgcolor: '#f0fdf4' } }}>
                                Add Line Item
                            </Button>
                            <Button variant="contained" startIcon={calculating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <CalculateIcon />}
                                onClick={handleCalculate} disabled={calculating || !calcForm.state}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                                Calculate
                            </Button>
                        </Box>
                    </Card>

                    {/* Calculation Results */}
                    {calcResult && (
                        <Card elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>Calculation Results</Typography>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f0fdf4' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Combined Rate</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>{calcResult.combined_rate ?? 0}%</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#dbeafe' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Total Tax</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(calcResult.total_tax)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#fef9c3' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Taxable Amount</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(calcResult.taxable_amount)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#fce7f3' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Total with Tax</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(calcResult.total_with_tax)}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Jurisdiction Breakdown */}
                            {calcResult.jurisdictions && calcResult.jurisdictions.length > 0 && (
                                <>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', mb: 1.5, color: '#475569' }}>Jurisdiction Breakdown</Typography>
                                    <Table size="small" sx={{ mb: 3 }}>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                {['Jurisdiction', 'Level', 'Rate', 'Tax Amount'].map(h => (
                                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {calcResult.jurisdictions.map((j, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell sx={bodyCellSx}>{j.jurisdiction_name}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{j.jurisdiction_level}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{j.rate}%</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(j.tax_amount)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            )}

                            {/* Per-Line-Item Tax */}
                            {calcResult.line_item_taxes && calcResult.line_item_taxes.length > 0 && (
                                <>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', mb: 1.5, color: '#475569' }}>Per-Line-Item Tax</Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                {['Description', 'Amount', 'Taxable', 'Tax'].map(h => (
                                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {calcResult.line_item_taxes.map((li, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell sx={bodyCellSx}>{li.description}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{formatCurrency(li.amount)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{li.is_taxable ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(li.tax)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            )}
                        </Card>
                    )}
                </TabPanel>
            </Box>

            {/* Add/Edit Rate Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{editData ? 'Edit Tax Rate' : 'Add Tax Rate'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Jurisdiction Name" required
                                value={formData.jurisdiction_name}
                                onChange={e => setFormData(prev => ({ ...prev, jurisdiction_name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Jurisdiction Level"
                                value={formData.jurisdiction_level}
                                onChange={e => setFormData(prev => ({ ...prev, jurisdiction_level: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {JURISDICTION_LEVELS.filter(l => l.value).map(l => <MenuItem key={l.value} value={l.value} sx={{ fontFamily }}>{l.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="State" required
                                value={formData.state_code}
                                onChange={e => setFormData(prev => ({ ...prev, state_code: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {US_STATES.filter(s => s.value).map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Rate (%)" type="number" required
                                value={formData.rate}
                                onChange={e => setFormData(prev => ({ ...prev, rate: e.target.value }))}
                                inputProps={{ step: 0.001, min: 0 }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth select label="Tax Type"
                                value={formData.tax_type}
                                onChange={e => setFormData(prev => ({ ...prev, tax_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {TAX_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Effective Date" type="date"
                                value={formData.effective_date}
                                onChange={e => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={formData.is_active}
                                        onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                        sx={{ '&.Mui-checked': { color: ACCENT } }} />
                                }
                                label={<Typography sx={{ fontFamily, fontWeight: 600 }}>Active</Typography>}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSaveRate} variant="contained" disabled={saving || !formData.jurisdiction_name || !formData.state_code}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (editData ? 'Update' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Tax Rate?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteId(null)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
